import type { ResultOf } from "gql.tada";
import type { ScheduledBriefingFragment } from "@/data-access/news-alerts/fragments/scheduled-briefing-fragments";
import type { TimezoneChoices } from "@/data-access/news-alerts/queries/timezone-queries";
import {
  to12HourTime,
  to24HourTime,
} from "@/store/news-alerts/scheduleTime.helpers";
import {
  type ScheduledBriefingModalState,
  scheduledBriefingLogoInitialState,
} from "./scheduled-briefing.slice";

type ScheduledBriefingInputState = Pick<
  ScheduledBriefingModalState,
  | "scheduledBriefingDetails"
  | "scheduledBriefingSchedule"
  | "scheduledBriefingRecipients"
  | "scheduledBriefingLogo"
>;

/**
 * Maps create-modal store state to `ScheduledBriefingInput`.
 */
export const buildScheduledBriefingInput = (
  state: ScheduledBriefingInputState,
) => {
  const { title, description, showDescription, prompt, hasUserPrompt } =
    state.scheduledBriefingDetails;
  const { scheduleFrequency, scheduleTime, timezone } =
    state.scheduledBriefingSchedule;
  const { validEmails } = state.scheduledBriefingRecipients;
  const { logoFile } = state.scheduledBriefingLogo;

  const input = {
    name: title.trim(),
    description: showDescription ? description : null,
    prompt: prompt.trim(),
    hasUserPrompt,
    recipients: validEmails,
    sendOn: scheduleFrequency,
    sendAt: to24HourTime(scheduleTime),
    timezone: timezone as TimezoneChoices,
    isActive: true,
  };

  return logoFile ? { ...input, logo: logoFile } : input;
};

/**
 * Maps create/edit modal store state to `GenerateBriefingPdfPreviewInput`.
 */
export const buildGenerateBriefingPdfPreviewInput = (
  state: Pick<
    ScheduledBriefingModalState,
    | "scheduledBriefingDetails"
    | "scheduledBriefingLogo"
    | "editingScheduledBriefingId"
  >,
) => {
  const { prompt, hasUserPrompt } = state.scheduledBriefingDetails;
  const { logoFile, previewUrl } = state.scheduledBriefingLogo;

  const input = { prompt: prompt.trim(), hasUserPrompt };

  if (logoFile) {
    return { ...input, logo: logoFile };
  }
  // Editing with an untouched saved logo: let the backend reuse it (previewUrl is cleared on removal).
  if (state.editingScheduledBriefingId && previewUrl) {
    return { ...input, scheduledBriefingId: state.editingScheduledBriefingId };
  }
  return input;
};

export type ScheduledBriefingFormHydration = ScheduledBriefingInputState;

export type ScheduledBriefingFromApi = ResultOf<
  typeof ScheduledBriefingFragment
>;

/**
 * Maps `ScheduledBriefingType` from the API into create/edit modal store fields.
 */
export const hydrateScheduledBriefingFormFromApi = (
  briefing: ScheduledBriefingFromApi,
): ScheduledBriefingFormHydration => {
  const description = briefing.description.trim();

  return {
    scheduledBriefingDetails: {
      title: briefing.name,
      description,
      showDescription: description.length > 0,
      prompt: briefing.prompt,
      hasUserPrompt: briefing.hasUserPrompt ?? false,
    },
    scheduledBriefingSchedule: {
      scheduleFrequency: briefing.sendOn,
      scheduleTime: to12HourTime(briefing.sendAt),
      timezone: briefing.timezone as TimezoneChoices,
    },
    scheduledBriefingRecipients: {
      validEmails: [...briefing.recipients],
      invalidEmails: [],
      inputValue: "",
    },
    scheduledBriefingLogo: {
      ...scheduledBriefingLogoInitialState,
      previewUrl: briefing.logoUrl ?? briefing.logo?.url ?? null,
      fileName: briefing.logo?.name ?? null,
    },
  };
};

/**
 * Maps create/edit modal store state to `ScheduledBriefingUpdateInput`.
 */
export const buildScheduledBriefingUpdateInput = (
  state: ScheduledBriefingInputState,
  scheduledBriefingId: string,
) => {
  const { title, description, showDescription, prompt, hasUserPrompt } =
    state.scheduledBriefingDetails;
  const { scheduleFrequency, scheduleTime, timezone } =
    state.scheduledBriefingSchedule;
  const { validEmails } = state.scheduledBriefingRecipients;
  const { logoFile, isRemoved } = state.scheduledBriefingLogo;

  const input = {
    scheduledBriefingId,
    name: title.trim(),
    description: showDescription ? description : null,
    prompt: prompt.trim(),
    hasUserPrompt,
    recipients: validEmails,
    sendOn: scheduleFrequency,
    sendAt: to24HourTime(scheduleTime),
    timezone: timezone as TimezoneChoices,
  };

  if (logoFile) {
    return { ...input, logo: logoFile };
  }

  if (isRemoved) {
    return { ...input, logo: null };
  }

  return input;
};
